import React from 'react';
import { styled } from '@compiled/react';
import truncate from 'lodash/truncate';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { singleFlag_softwareReleasesVersionDetailIssueList$key } from '@atlassian/jira-relay/src/__generated__/singleFlag_softwareReleasesVersionDetailIssueList.graphql';
import { VIEWPORT_WIDTH_MEDIUM } from '../../../../../common/constants.tsx';
import { FlagDisabledIcon, FlagEnabledIcon } from './icons/FlagDisabledIcon.tsx';
import messages from './messages.tsx';

const MAX_DISPLAY_NAME_LENGTH = 255;

type Props = {
	summarisedFlags: singleFlag_softwareReleasesVersionDetailIssueList$key;
};

export const SingleFlag = ({ summarisedFlags }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const { mostRelevantDisplayName, mostRelevantEnabled, mostRelevantRolloutPercentage } =
		useFragment(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment singleFlag_softwareReleasesVersionDetailIssueList on DevOpsSummarisedFeatureFlags {
					mostRelevantDisplayName @required(action: THROW)
					mostRelevantEnabled @required(action: THROW)
					mostRelevantRolloutPercentage
				}
			`,
			summarisedFlags,
		);

	const truncatedDisplayName =
		mostRelevantDisplayName.length > MAX_DISPLAY_NAME_LENGTH
			? truncate(mostRelevantDisplayName, { length: MAX_DISPLAY_NAME_LENGTH, separator: ' ' })
			: mostRelevantDisplayName;

	const renderTooltip = () => {
		if (!mostRelevantEnabled) {
			return formatMessage(messages.flagDisabled, {
				displayName: truncatedDisplayName,
			});
		}

		if (mostRelevantRolloutPercentage == null) {
			return formatMessage(messages.flagWithoutPercentage, {
				displayName: truncatedDisplayName,
			});
		}

		return formatMessage(messages.flagWithPercentage, {
			displayName: truncatedDisplayName,
			percentage: mostRelevantRolloutPercentage / 100,
		});
	};

	return (
		<Tooltip
			testId="software-releases-version-detail-issue-list.ui.issues.issue-card.feature-flags.single-flag.tooltip"
			content={renderTooltip()}
			onShow={(analyticsEvent: UIAnalyticsEvent) => {
				fireUIAnalytics(analyticsEvent, 'featureFlagSummaryTooltip');
			}}
			position="bottom-start"
		>
			<Container>
				{mostRelevantEnabled ? <FlagEnabledIcon /> : <FlagDisabledIcon />}
				<TextContainer>
					{mostRelevantRolloutPercentage != null
						? `${formatMessage(messages.flagPercentageRollout, {
								featureFlagPercentageRollout: mostRelevantRolloutPercentage / 100,
							})}`
						: null}
				</TextContainer>
			</Container>
		</Tooltip>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'inline',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		display: 'none',
	},
});
